import { useEffect, useState } from 'react';
import { useThemeStore } from '@/store/themeStore';

/**
 * Hook to manage dark mode theme
 * Handles system preference detection and applies theme to document root
 */
export function useTheme() {
  const { theme, resolvedTheme, setTheme, setResolvedTheme, toggleTheme } = useThemeStore();
  const [hasHydrated, setHasHydrated] = useState(false);

  // Wait for Zustand to hydrate from localStorage before applying theme
  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    // Skip theme application until store has hydrated from localStorage
    if (!hasHydrated) return;

    // Function to get system preference
    const getSystemTheme = (): 'light' | 'dark' => {
      if (typeof window === 'undefined') return 'light';
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    };

    // Function to apply theme to document
    const applyTheme = (newTheme: 'light' | 'dark') => {
      const root = document.documentElement;

      if (newTheme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      setResolvedTheme(newTheme);
    };

    // Determine which theme to apply
    const themeToApply = theme === 'system' ? getSystemTheme() : theme;
    applyTheme(themeToApply);

    // Listen for system theme changes (only if user selected 'system')
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        applyTheme(e.matches ? 'dark' : 'light');
      };

      // Use addEventListener instead of deprecated addListener
      mediaQuery.addEventListener('change', handleChange);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [theme, setResolvedTheme, hasHydrated]);

  return {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark',
  };
}
